import MonacoEditor from '@monaco-editor/react';
import { FormBuilder } from '@nirmata/core';
import { EXAMPLES as EXAMPLES_LIST } from '@nirmata/react-form-examples';
import type { TFormField } from '@nirmata/shared';
import { CheckboxField } from '@nirmata/web-components/basic/checkbox-field';
import { TextField } from '@nirmata/web-components/basic/text-field';
import { TextAreaField } from '@nirmata/web-components/basic/textarea-field';
import { RadioField } from '@nirmata/web-components/choice/radio-field';
import { SelectField } from '@nirmata/web-components/choice/select-field';
import { Container } from '@nirmata/web-components/layout/container';
import { Section } from '@nirmata/web-components/layout/section';
import { Button } from '@nirmata/web-components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@nirmata/web-components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nirmata/web-components/ui/dropdown-menu';
import { ChevronDown, Download, Eye, FileJson } from 'lucide-react';
import type * as Monaco from 'monaco-editor';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { NIRMATA_DARK_THEME, NIRMATA_LIGHT_THEME } from '@/components/theme/monacoThemes';
import { useTheme } from '@/components/theme/ThemeProvider';
import { useBuilderStore } from '@/store/builder';

const defaultComponentMapper = {
  container: Container,
  section: Section,
  'text-field': TextField,
  'number-field': TextField,
  'select-field': SelectField,
  'checkbox-field': CheckboxField,
  'radio-field': RadioField,
  'textarea-field': TextAreaField,
};

// Define the type for an example item
export type ExampleItem = {
  label: string;
  value: string;
  preview?: React.ComponentType<any>;
  schema: TFormField;
};

export function FormPreview() {
  const { schema, setSchema, selectedExample, setSelectedExample, cachedSchema, setCachedSchema } =
    useBuilderStore();
  const formInstance = useForm({
    mode: 'onChange',
    shouldUnregister: true,
  });
  const [showJson, setShowJson] = useState(false);
  const { theme } = useTheme();
  const monacoRef = useRef<typeof Monaco | null>(null);

  let nirmataThemesDefined = false;

  const handleEditorDidMount = (
    editor: Monaco.editor.IStandaloneCodeEditor,
    monaco: typeof Monaco
  ) => {
    monacoRef.current = monaco;
    if (monaco && monaco.editor) {
      if (!nirmataThemesDefined) {
        monaco.editor.defineTheme('nirmata-light', {
          ...NIRMATA_LIGHT_THEME,
          base: 'vs',
        });
        monaco.editor.defineTheme('nirmata-dark', {
          ...NIRMATA_DARK_THEME,
          base: 'vs-dark',
        });
        nirmataThemesDefined = true;
      }
      monaco.editor.setTheme(theme === 'dark' ? 'nirmata-dark' : 'nirmata-light');
    }
  };

  useEffect(() => {
    if (monacoRef.current && monacoRef.current.editor) {
      monacoRef.current.editor.setTheme(theme === 'dark' ? 'nirmata-dark' : 'nirmata-light');
    }
  }, [theme]);

  // Update: always download the current schema
  const downloadJson = () => {
    if (!schema) return;
    const data = JSON.stringify(schema, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `form-schema-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const [submittedData, setSubmittedData] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const submit = (data: Record<string, unknown>) => {
    setSubmittedData(JSON.stringify(data, null, 2));
    setIsDialogOpen(true);
  };

  return (
    <div className="flex flex-col h-full">
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Form Submission Data</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            <MonacoEditor
              height="100%"
              defaultLanguage="json"
              value={submittedData || ''}
              options={{
                readOnly: true,
                minimap: { enabled: true },
                scrollBeyondLastLine: false,
                wordWrap: 'on',
              }}
              onMount={handleEditorDidMount}
              theme={theme === 'dark' ? 'nirmata-dark' : 'nirmata-light'}
            />
          </div>
        </DialogContent>
      </Dialog>
      <div className="space-y-2 mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-base font-medium tracking-tight">Preview</h2>
          <p className="text-sm text-[var(--muted-foreground)]">
            Preview how your form will look to users
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="min-w-[160px] justify-between">
                {selectedExample ? selectedExample.label : 'Select example'}
                <ChevronDown className="ml-2 w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => {
                  setSelectedExample(null);
                  if (cachedSchema) {
                    setSchema(cachedSchema as TFormField);
                  }
                }}
                className={
                  !selectedExample ? 'bg-[var(--accent)] text-[var(--accent-foreground)]' : ''
                }
              >
                Clear selection
              </DropdownMenuItem>
              {EXAMPLES_LIST.map((ex: ExampleItem) => (
                <DropdownMenuItem
                  key={ex.value}
                  onSelect={() => {
                    if (!selectedExample) {
                      setCachedSchema(schema);
                    }
                    setSelectedExample(ex);
                    setSchema(ex.schema);
                  }}
                  className={
                    selectedExample && selectedExample.value === ex.value
                      ? 'bg-[var(--accent)] text-[var(--accent-foreground)]'
                      : ''
                  }
                >
                  {ex.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            className="flex items-center justify-stretch gap-1.5 cursor-pointer hover:bg-[var(--primary)]/20 hover:text-[var(--primary)]"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setShowJson((v) => !v);
            }}
            title={showJson ? 'Show Form Preview' : 'Show JSON'}
          >
            {showJson ? <Eye className="w-4 h-4" /> : <FileJson className="w-4 h-4" />}
          </Button>
          {!!schema && (
            <Button
              className="flex items-center gap-1.5 cursor-pointer hover:bg-[var(--primary)] hover:text-[var(--primary-foreground)]"
              variant="outline"
              size="sm"
              onClick={downloadJson}
              title="Download JSON"
              disabled={!schema}
            >
              <Download className="w-4 h-4" />
              <span>Download JSON</span>
            </Button>
          )}
        </div>
      </div>
      <div className="relative rounded-lg border border-[var(--border)] p-4 overflow-y-auto flex-1 min-h-0">
        {showJson ? (
          <MonacoEditor
            height="100%"
            defaultLanguage="json"
            value={JSON.stringify(selectedExample ? selectedExample.schema : schema, null, 2)}
            onMount={handleEditorDidMount}
            options={{
              readOnly: true,
              minimap: { enabled: false },
              fontSize: 14,
              fontFamily: 'Fira Code',
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              theme: theme === 'dark' ? 'nirmata-dark' : 'nirmata-light',
            }}
          />
        ) : selectedExample ? (
          selectedExample.preview ? (
            <div className="flex flex-col gap-4">
              <selectedExample.preview formInstance={formInstance} />
              <div className="flex justify-end">
                <Button
                  className="w-full sm:w-auto justify-center cursor-pointer"
                  variant="default"
                  size="sm"
                  type="submit"
                  disabled={
                    formInstance.formState.isSubmitting ||
                    !selectedExample?.schema?.childFields?.length
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    formInstance.handleSubmit(submit)(e);
                  }}
                >
                  Submit
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-[var(--muted-foreground)]">
                Form preview will appear here
              </p>
            </div>
          )
        ) : !schema ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-[var(--muted-foreground)]">Form preview will appear here</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <FormBuilder
              schema={schema}
              componentMapper={defaultComponentMapper}
              propsMapper={{}}
              formInstance={formInstance}
            />
            <div className="flex justify-end">
              <Button
                className="w-full sm:w-auto justify-center cursor-pointer"
                variant="default"
                size="sm"
                type="submit"
                disabled={formInstance.formState.isSubmitting || !schema?.childFields?.length}
                onClick={(e) => {
                  e.stopPropagation();
                  formInstance.handleSubmit(submit)(e);
                }}
              >
                Submit
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
